import React from 'react'
import { FormikErrors, FormikTouched } from 'formik'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { ActionButton, Dropdown, TextInput } from 'components'
import useWorkFlowLookup from 'hooks/lookup/use-workflow-lookup'

export type FormValues = {
  rule_id?: number
  campaign_id?: string
  campaign?: string
  lead_source_id?: string | null
  lead_source?: string | null
  user_id: string | null
  team_id: string | null
  owner?: string | null
  lead_process_id?: string | null
  lead_process?: string | null
  is_default?: boolean
}

type Props = {
  values: FormValues
  onChange?: (v: FormValues) => void
  onDelete?: () => void
  onDefaultClick?: (checked: boolean) => void
  errors?: FormikErrors<FormValues>
  touched?: FormikTouched<FormValues>
  isDisabled?: boolean
  canDelete?: boolean
}

const ApiKeyForm: React.FC<Props> = ({ values, onChange, onDelete, errors, touched, isDisabled, canDelete }) => {
  const { t } = useTranslation()
  const { leadCampaignOptions, leadSourceOptions, leadProcessOptions, userTeamOptions } = useWorkFlowLookup({
    leadCampaignId: values.campaign_id,
    onlyValidCampaigns: false,
    sendAllCampaignsAsDefault: true,
    includeUnassigned: true,
    queryOptions: {
      staleTime: 0,
    },
  })

  const handleChange = (v: Partial<FormValues>) => {
    onChange?.({ ...values, ...v })
  }

  const selectedCampaign =
    leadCampaignOptions.find((i) => i.value === values.campaign_id) ??
    (values.campaign_id
      ? {
          label: values.campaign ?? '',
          value: values.campaign_id,
        }
      : undefined)

  const selectedSource =
    leadSourceOptions.find((i) => i.value === values.lead_source_id) ??
    (values.lead_source_id ? { label: values.lead_source ?? '', value: values.lead_source_id } : undefined)

  const selectedProcess =
    leadProcessOptions.find((i) => i.value === values.lead_process_id) ??
    (values.lead_process_id
      ? {
          label: values.lead_process ?? '',
          value: values.lead_process_id,
        }
      : undefined)

  const selectedOwner =
    userTeamOptions.find((i) => i.value === values.user_id || i.value === values.team_id) ??
    (values.user_id || values.team_id
      ? {
          label: values.owner ?? '',
          value: values.user_id ?? values.team_id,
        }
      : { label: t('unassigned_lbl'), value: '' })

  return (
    <RulesContainer>
      <Dropdown
        name="campaign_id"
        variant="labeled"
        label="campaign_lbl"
        options={leadCampaignOptions}
        required
        onChange={(v) => {
          onChange?.({
            ...values,
            campaign_id: v.value,
            campaign: v.label,
            lead_source_id: v.data.lead_source_id,
            lead_source: v.data.lead_source,
            lead_process_id: v.data.lead_process_id,
            lead_process: v.data.lead_process,
          })
        }}
        value={selectedCampaign}
        error={touched?.campaign_id ? errors?.campaign_id : undefined}
        isDisabled={isDisabled}
      />

      {values.campaign_id && (
        <>
          <TextInput
            variant="labeled"
            label="lead_source_lbl"
            value={values.lead_source ?? ' '}
            readOnly
            my="1rem"
            error={touched?.lead_source_id ? errors?.lead_source_id : undefined}
          />
          <TextInput
            variant="labeled"
            label="sales_pipeline_lbl"
            value={values.lead_process ?? ' '}
            readOnly
            my="1rem"
            error={touched?.lead_process_id ? errors?.lead_process_id : undefined}
          />
        </>
      )}

      {!values.campaign_id && (
        <>
          <Dropdown
            name="lead_source_id"
            variant="labeled"
            label="lead_source_lbl"
            options={leadSourceOptions}
            required
            onChange={(v) => {
              onChange?.({
                ...values,
                lead_source_id: v.value,
                lead_source: v.label,
              })
            }}
            value={selectedSource}
            error={touched?.lead_source_id ? errors?.lead_source_id : undefined}
            isDisabled={isDisabled}
            my="1rem"
          />
          <Dropdown
            name="process_id"
            variant="labeled"
            label="sales_pipeline_lbl"
            options={leadProcessOptions}
            required
            onChange={(v) => {
              onChange?.({
                ...values,
                lead_process_id: v.value,
                lead_process: v.label,
              })
            }}
            value={selectedProcess}
            error={touched?.lead_process_id ? errors?.lead_process_id : undefined}
            isDisabled={isDisabled}
            my="1rem"
          />
        </>
      )}

      <Dropdown
        name="owner"
        variant="labeled"
        label="lead_owner_lbl"
        options={userTeamOptions}
        required
        onChange={(v) => {
          handleChange({
            user_id: v.data === 'user' ? v.value : null,
            team_id: v.data === 'team' ? v.value : null,
          })
        }}
        value={selectedOwner}
        error={touched?.team_id ? errors?.team_id : undefined}
        isDisabled={isDisabled}
        my="1rem"
      />
      {/*TODO: enable this when support multiple rules 
      <Checkbox mt="1rem" checked={values.is_default} onChange={(checked) => onDefaultClick?.(checked)}>
        Default rule
      </Checkbox> */}
      {!isDisabled && canDelete && (
        <ButtonContainer>
          <ActionButton variant="delete" isIconOnly onClick={() => onDelete?.()} />
        </ButtonContainer>
      )}
    </RulesContainer>
  )
}

const RulesContainer = styled.div`
  position: relative;
  border: 1px solid ${({ theme }) => theme.colors.defaultBorder};
  border-radius: 4px;
  margin-top: 1rem;
  padding: 2rem 1rem 1rem;
`

const ButtonContainer = styled.div`
  position: absolute;
  right: 1rem;
  top: 1rem;
`

export default ApiKeyForm
